/**
 * Reset Password Dialog
 * 
 * Dialog component for resetting a user's password.
 * Allows admin to choose between using IAR value or custom password.
 */

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { PasswordTypeSelector } from "./components/PasswordTypeSelector";
import { useResetPassword } from "./hooks/useResetPassword";

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userIar?: string;
  userName?: string;
  callsign?: string;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userIar,
  userName,
  callsign,
}: ResetPasswordDialogProps) {
  const resetPassword = useResetPassword({
    userId,
    userIar,
    userName,
    callsign,
    onSuccess: () => {
      // Close dialog after 1.5 seconds on success
      setTimeout(() => {
        onOpenChange(false);
      }, 1500);
    },
  });

  const { resetState } = resetPassword;

  // Reset form only when the dialog opens (not on every re-render — the hook return object is new each render).
  React.useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Password</AlertDialogTitle>
          <AlertDialogDescription>
            {userName && `Reset password untuk ${callsign} |  ${userName}`}
            {!userName && "Pilih metode reset password"}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <PasswordTypeSelector
            passwordType={resetPassword.passwordType}
            onPasswordTypeChange={resetPassword.setPasswordType}
            userIar={userIar}
            customPassword={resetPassword.customPassword}
            onCustomPasswordChange={resetPassword.setCustomPassword}
            showPassword={resetPassword.showPassword}
            onShowPasswordToggle={() => resetPassword.setShowPassword(!resetPassword.showPassword)}
          />

          {resetPassword.error && (
            <div className="text-sm text-red-600 dark:text-red-400 mt-2">
              {resetPassword.error}
            </div>
          )}

          {resetPassword.success && (
            <div className="text-sm text-green-600 dark:text-green-400 mt-2">
              Password berhasil direset!
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={resetPassword.loading}>
            Batal
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={resetPassword.handleSubmit}
            disabled={!resetPassword.canSubmit}
          >
            {resetPassword.loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Memproses...
              </>
            ) : (
              "Reset Password"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

